import { useEffect, useState, useCallback } from "react";
import { Shield, Loader2, Users, Crown, Sparkles, ShieldCheck, DollarSign, Percent, Gift, Ticket, Plus, Trash2, Power } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

const ROLE_BADGE = {
  superadmin: "bg-purple-500/15 text-purple-300",
  admin: "bg-blue-500/15 text-blue-300",
  user: "bg-slate-700/40 text-slate-400",
};
const PLAN_BADGE = {
  premium: "bg-amber-500/15 text-amber-300",
  basic: "bg-blue-500/15 text-blue-300",
  free: "bg-slate-700/40 text-slate-400",
};

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-[#0E1118] p-4">
      <div className="mb-1 flex items-center gap-2 text-xs text-slate-500"><Icon className="h-4 w-4" /> {label}</div>
      <div className="font-heading text-2xl font-bold text-white">{value}</div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const { t } = useLang();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [promos, setPromos] = useState([]);
  const [newCode, setNewCode] = useState("");
  const [newPct, setNewPct] = useState("");
  const [newMax, setNewMax] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, s, p] = await Promise.all([api.get("/admin/users"), api.get("/admin/stats"), api.get("/admin/promos")]);
      setUsers(u.data.users);
      setStats(s.data);
      setPromos(p.data.promos || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createPromo = async () => {
    if (!newCode.trim() || !newPct) { toast.error(t("admin.codeAndPct")); return; }
    setPromoBusy(true);
    try {
      await api.post("/admin/promos", { code: newCode.trim(), percent_off: Number(newPct), max_redemptions: newMax ? Number(newMax) : null });
      toast.success(t("admin.promoCreated"));
      setNewCode(""); setNewPct(""); setNewMax("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPromoBusy(false); }
  };

  const togglePromo = async (code, active) => {
    try { await api.put(`/admin/promos/${code}`, { active: !active }); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deletePromo = async (code) => {
    if (!window.confirm(t("admin.confirmDeletePromo", { code }))) return;
    try { await api.delete(`/admin/promos/${code}`); toast.success(t("admin.codeDeleted")); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setRole = async (uid, role) => {
    setBusy(uid);
    try {
      await api.put(`/admin/users/${uid}/role`, { role });
      toast.success(role === "admin" ? t("admin.promoted") : t("admin.demoted"));
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const setPlan = async (uid, plan) => {
    setBusy(uid);
    try {
      await api.put(`/admin/users/${uid}/plan`, { plan });
      toast.success(t("admin.planUpdated"));
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const resetPwd = async (uid, email) => {
    if (!window.confirm(t("admin.confirmResetPwd", { email }))) return;
    setBusy(uid);
    try {
      const { data } = await api.put(`/admin/users/${uid}/reset-password`, {});
      window.prompt(t("admin.tempPwdPrompt", { email }), data.temporary_password);
      toast.success(t("admin.pwdResetOk"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const deleteUser = async (uid, email) => {
    if (!window.confirm(t("admin.confirmDeleteUser", { email }))) return;
    setBusy(uid);
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success(t("admin.accountDeleted"));
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  return (
    <div>
      <PageHeader title={t("admin.panelTitle")} subtitle={t("admin.panelSub")} icon={Shield} />

      {stats && (
        <>
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard icon={Users} label={t("admin.users")} value={stats.total_users} />
          <StatCard icon={Sparkles} label={t("admin.basic")} value={stats.basic} />
          <StatCard icon={Crown} label={t("admin.premium")} value={stats.premium} />
          <StatCard icon={ShieldCheck} label={t("admin.adminsLabel")} value={stats.admins} />
        </div>
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="revenue-stats">
          <StatCard icon={DollarSign} label={t("admin.revenueTotal")} value={`$${(stats.revenue_total ?? 0).toFixed(2)}`} />
          <StatCard icon={DollarSign} label={t("admin.revenue30")} value={`$${(stats.revenue_30d ?? 0).toFixed(2)}`} />
          <StatCard icon={Percent} label={t("admin.conversion")} value={`${stats.conversion_pct ?? 0}%`} />
          <StatCard icon={Gift} label={t("admin.referred")} value={stats.referred_users ?? 0} />
        </div>
        </>
      )}

      {/* Codes promo */}
      <div className="mb-6 rounded-xl border border-slate-800 bg-[#0E1118] p-5" data-testid="promo-admin">
        <div className="mb-3 flex items-center gap-2"><Ticket className="h-4 w-4 text-amber-400" /><h3 className="font-heading text-sm font-bold text-white">{t("admin.promos")}</h3></div>
        <div className="mb-4 flex flex-wrap items-end gap-2">
          <input value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} placeholder={t("admin.phCode")} data-testid="promo-code-input"
            className="w-32 rounded-lg border border-slate-700 bg-[#11141A] px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500" />
          <input value={newPct} onChange={(e) => setNewPct(e.target.value)} type="number" placeholder={t("admin.phPct")} data-testid="promo-pct-input"
            className="w-24 rounded-lg border border-slate-700 bg-[#11141A] px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500" />
          <input value={newMax} onChange={(e) => setNewMax(e.target.value)} type="number" placeholder={t("admin.phMax")} data-testid="promo-max-input"
            className="w-36 rounded-lg border border-slate-700 bg-[#11141A] px-3 py-2 text-sm text-slate-200 outline-none focus:border-blue-500" />
          <button onClick={createPromo} disabled={promoBusy} data-testid="promo-create-btn"
            className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60">
            {promoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("admin.create")}
          </button>
        </div>
        {promos.length === 0 ? (
          <p className="text-sm text-slate-500">{t("admin.noPromo")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="promo-table">
              <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                <tr><th className="py-2 text-left">{t("admin.phCode")}</th><th className="py-2 text-left">{t("admin.discount")}</th><th className="py-2 text-left">{t("admin.uses")}</th><th className="py-2 text-left">{t("admin.status")}</th><th className="py-2 text-right">{t("admin.actions")}</th></tr>
              </thead>
              <tbody>
                {promos.map((p) => (
                  <tr key={p.code} className="border-t border-slate-800" data-testid={`promo-row-${p.code}`}>
                    <td className="py-2.5 font-mono font-semibold text-slate-200">{p.code}</td>
                    <td className="py-2.5 text-slate-300">−{p.percent_off}%</td>
                    <td className="py-2.5 text-slate-400">{p.redeemed_count || 0}{p.max_redemptions ? ` / ${p.max_redemptions}` : ""}</td>
                    <td className="py-2.5"><span className={`rounded px-2 py-0.5 text-xs font-semibold ${p.active ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-700/40 text-slate-400"}`}>{p.active ? t("admin.active") : t("admin.inactive")}</span></td>
                    <td className="py-2.5">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => togglePromo(p.code, p.active)} data-testid={`promo-toggle-${p.code}`} className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"><Power className="h-3.5 w-3.5" /></button>
                        <button onClick={() => deletePromo(p.code)} data-testid={`promo-delete-${p.code}`} className="rounded border border-red-800/60 bg-red-500/10 px-2 py-1 text-xs text-red-300 hover:bg-red-500/20"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>


      {loading ? (
        <div className="flex items-center gap-2 p-6 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-800">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-[#0E1118] text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">{t("admin.user")}</th>
                <th className="px-4 py-3 text-left">{t("admin.role")}</th>
                <th className="px-4 py-3 text-left">{t("admin.planCol")}</th>
                <th className="px-4 py-3 text-right">{t("admin.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSuper = u.role === "superadmin";
                const isSelf = u.user_id === user?.user_id;
                return (
                  <tr key={u.user_id} className="border-t border-slate-800 hover:bg-[#161A22]" data-testid={`user-row-${u.email}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-200">{u.name}</div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                    </td>
                    <td className="px-4 py-3"><span className={`rounded px-2 py-0.5 text-xs font-semibold ${ROLE_BADGE[u.role]}`}>{u.role}</span></td>
                    <td className="px-4 py-3"><span className={`rounded px-2 py-0.5 text-xs font-semibold ${PLAN_BADGE[u.plan]}`}>{u.plan === "free" ? t("admin.free") : t(`plan.${u.plan}`)}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {busy === u.user_id && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
                        {!isSuper && !isSelf && (
                          u.role === "admin" ? (
                            <button data-testid={`demote-${u.email}`} onClick={() => setRole(u.user_id, "user")} disabled={busy}
                              className="rounded border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-800">{t("admin.removeAdmin")}</button>
                          ) : (
                            <button data-testid={`promote-${u.email}`} onClick={() => setRole(u.user_id, "admin")} disabled={busy}
                              className="rounded border border-blue-700 bg-blue-600/10 px-2.5 py-1 text-xs text-blue-300 hover:bg-blue-600/20">{t("admin.promoteAdmin")}</button>
                          )
                        )}
                        {!isSuper && (
                          <select data-testid={`plan-select-${u.email}`} value={u.plan} onChange={(e) => setPlan(u.user_id, e.target.value)} disabled={busy}
                            className="rounded border border-slate-700 bg-[#11141A] px-2 py-1 text-xs text-slate-300 outline-none">
                            <option value="free">{t("admin.free")}</option>
                            <option value="basic">{t("admin.basic")}</option>
                            <option value="premium">{t("admin.premium")}</option>
                          </select>
                        )}
                        {!isSuper && (
                          <button data-testid={`reset-pwd-${u.email}`} onClick={() => resetPwd(u.user_id, u.email)} disabled={busy}
                            className="rounded border border-amber-800/60 bg-amber-500/10 px-2.5 py-1 text-xs text-amber-300 hover:bg-amber-500/20">
                            {t("admin.resetPwd")}
                          </button>
                        )}
                        {!isSuper && !isSelf && (
                          <button data-testid={`delete-user-${u.email}`} onClick={() => deleteUser(u.user_id, u.email)} disabled={busy}
                            title={t("admin.deleteAccountTitle")}
                            className="flex items-center gap-1 rounded border border-red-800/60 bg-red-500/10 px-2.5 py-1 text-xs text-red-300 hover:bg-red-500/20">
                            <Trash2 className="h-3.5 w-3.5" /> {t("admin.deleteBtn")}
                          </button>
                        )}
                        {isSuper && <span className="text-xs text-slate-600">{t("admin.superadmin")}</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
